import {
  Box,
  Flex,
  Grid,
  GridItem,
  HStack,
  Image,
  Link,
  SimpleGrid,
  Skeleton,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ReactNode } from "react";
import { ThemeColor } from "../../theme/palette";
import { DripVaultState } from "../../hooks/useDripVaultState";
import { formatAddress, formatSmartFixed6 } from "../../utils/format";
import { useDripVaultIcon } from "./useDripVaultIcon";
import {
  DRIP_BOX_BG,
  DRIP_BORDER,
  DRIP_TEXT_LABEL,
  DRIP_TEXT_MUTED,
  DRIP_TEXT_SECONDARY,
} from "./dripStyles";

interface DripDashboardSectionProps {
  vaultState: DripVaultState | null;
  isLoading: boolean;
}

const usd = (value: number) =>
  `$${value.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const StatTile = ({
  label,
  children,
  loading,
}: {
  label: string;
  children: ReactNode;
  loading: boolean;
}) => (
  <GridItem
    bg="rgba(255, 255, 255, 0.02)"
    borderRadius="lg"
    border="1px solid"
    borderColor="whiteAlpha.100"
    px={4}
    py={4}
  >
    <Text
      fontSize="xs"
      fontWeight="medium"
      color={DRIP_TEXT_LABEL}
      letterSpacing="0.08em"
      textTransform="uppercase"
    >
      {label}
    </Text>
    <Box mt={2} minH="24px">
      {loading ? <Skeleton height="20px" width="90px" /> : children}
    </Box>
  </GridItem>
);

const AddressRow = ({
  label,
  address,
  loading,
}: {
  label: string;
  address: string | undefined;
  loading: boolean;
}) => (
  <Flex justify="space-between" align="center" py={2}>
    <Text fontSize="sm" color={DRIP_TEXT_MUTED}>
      {label}
    </Text>
    {loading || !address ? (
      <Skeleton height="16px" width="110px" />
    ) : (
      <Link
        href={`https://solscan.io/account/${address}`}
        isExternal
        fontSize="sm"
        fontFamily="mono"
        color={DRIP_TEXT_SECONDARY}
        _hover={{ color: ThemeColor.KAIROS }}
      >
        {formatAddress(address)}
      </Link>
    )}
  </Flex>
);

export const DripDashboardSection = ({ vaultState, isLoading }: DripDashboardSectionProps) => {
  const vaultIcon = useDripVaultIcon(vaultState?.vaultId, vaultState?.depositMint);

  return (
    <Box
      bg={DRIP_BOX_BG}
      borderRadius="xl"
      border="1px solid"
      borderColor={DRIP_BORDER}
      overflow="hidden"
      w="100%"
    >
      {/* Header: identity + TVL headline + status */}
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "flex-start", md: "center" }}
        gap={4}
        px={{ base: 5, md: 6 }}
        py={5}
        borderBottom="1px solid"
        borderColor="whiteAlpha.100"
      >
        <HStack spacing={3} align="center">
          {vaultIcon && (
            <Image
              src={vaultIcon}
              alt={vaultState?.symbol ?? "Drip"}
              boxSize="36px"
              borderRadius="full"
            />
          )}
          <VStack align="start" spacing={0}>
            <Text fontSize="lg" fontWeight="semibold" color="white">
              Drip Vault
            </Text>
            <Text fontSize="xs" color={DRIP_TEXT_MUTED}>
              {vaultState?.symbol ?? "—"} · streaming rewards
            </Text>
          </VStack>
        </HStack>

        <HStack spacing={5} align="center">
          <VStack align={{ base: "start", md: "end" }} spacing={0}>
            <Text fontSize="xs" color={DRIP_TEXT_LABEL} letterSpacing="0.08em">
              TVL
            </Text>
            {isLoading ? (
              <Skeleton height="26px" width="140px" mt={1} />
            ) : (
              <HStack spacing={2} align="baseline">
                <Text fontSize="2xl" fontWeight="semibold" color={ThemeColor.KAIROS}>
                  {vaultState ? formatSmartFixed6(vaultState.staked) : "—"} {vaultState?.symbol ?? ""}
                </Text>
              </HStack>
            )}
            {!isLoading && vaultState?.stakedUsd != null && (
              <Text fontSize="sm" color={DRIP_TEXT_LABEL}>
                ~{usd(vaultState.stakedUsd)}
              </Text>
            )}
          </VStack>

          {!isLoading && vaultState && (
            <HStack
              px={3}
              py={1.5}
              borderRadius="full"
              spacing={2}
              bg={vaultState.paused ? "rgba(255, 93, 83, 0.1)" : "rgba(95, 244, 244, 0.08)"}
              border="1px solid"
              borderColor={
                vaultState.paused ? "rgba(255, 93, 83, 0.3)" : "rgba(95, 244, 244, 0.25)"
              }
            >
              <Box
                boxSize="8px"
                borderRadius="full"
                bg={vaultState.paused ? ThemeColor.ACTION_ERROR : ThemeColor.KAIROS}
              />
              <Text
                fontSize="xs"
                fontWeight="semibold"
                color={vaultState.paused ? ThemeColor.ACTION_ERROR : ThemeColor.KAIROS}
              >
                {vaultState.paused ? "Paused" : "Active"}
              </Text>
            </HStack>
          )}
        </HStack>
      </Flex>

      {/* Metrics queried from the vault state */}
      <Box px={{ base: 5, md: 6 }} py={5}>
        <Grid templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }} gap={3}>
          <StatTile label="Outstanding shares" loading={isLoading}>
            <Text fontSize="md" fontWeight="semibold" color="white">
              {vaultState
                ? formatSmartFixed6(vaultState.shareSupply ?? vaultState.totalAssets)
                : "—"}
            </Text>
          </StatTile>
          <StatTile label={`${vaultState?.symbol ?? "Vault"} rate`} loading={isLoading}>
            <Text fontSize="md" fontWeight="semibold" color="white">
              {vaultState ? formatSmartFixed6(vaultState.rate) : "—"}
            </Text>
          </StatTile>
          <StatTile label="Latest pending epoch" loading={isLoading}>
            <Text fontSize="md" fontWeight="semibold" color="white">
              {vaultState ? vaultState.latestPendingEpoch : "—"}
            </Text>
          </StatTile>
          <StatTile label="Pending shares (latest)" loading={isLoading}>
            <Text fontSize="md" fontWeight="semibold" color="white">
              {vaultState ? formatSmartFixed6(vaultState.pendingSharesLatest) : "—"}
            </Text>
          </StatTile>
          <StatTile label="Pending shares (previous)" loading={isLoading}>
            <Text fontSize="md" fontWeight="semibold" color="white">
              {vaultState ? formatSmartFixed6(vaultState.pendingSharesPrevious) : "—"}
            </Text>
          </StatTile>
        </Grid>

        {/* Authorities & mints */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={8} mt={5}>
          <Box>
            <AddressRow label="Admin" address={vaultState?.admin} loading={isLoading} />
            <AddressRow label="Swapper" address={vaultState?.swapper} loading={isLoading} />
            <AddressRow
              label="Fee recipient"
              address={vaultState?.feeRecipient}
              loading={isLoading}
            />
          </Box>
          <Box>
            <AddressRow label="Share mint" address={vaultState?.shareMint} loading={isLoading} />
            <AddressRow
              label={`${vaultState?.symbol ?? "Deposit"} mint`}
              address={vaultState?.depositMint}
              loading={isLoading}
            />
            <AddressRow
              label={vaultState?.kyrosVault ? "Kyros vault" : "Stake pool"}
              address={vaultState?.kyrosVault ?? vaultState?.stakePool}
              loading={isLoading}
            />
          </Box>
        </SimpleGrid>
      </Box>
    </Box>
  );
};
